import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';
import type { GroupBase, InputActionMeta } from 'react-select';
import * as styles from './EntitySelect.module.scss';
import EntitySelectMultiValue from './EntitySelectMultiValue';
import EntitySelectOption from './EntitySelectOption';
import {
  groupToEntity,
  postToEntity,
  topicToEntity,
  userToEntity,
} from './localEntitySearch';
import type { NewSelectOptionProps } from './NewSelectOption';
import type {
  UseEntitySearchConfig,
  UseEntitySearchGroupResult,
  UseEntitySearchPostResult,
  UseEntitySearchTopicResult,
  UseEntitySearchUserResult,
} from './useEntitySearch';
import useEntitySearch from './useEntitySearch';
import type { SelectProps, ValueType } from '../../components/Select';
import Select from '../../components/Select';
import { useAllGroupMap } from '../../groups/useAllGroups';
import { useAllSlimPostMap } from '../../post/useAllSlimPosts';
import { useAllTopicMap } from '../../topic/useAllTopics';
import { useAllUsersMap } from '../../user/useAllUsers';

export type EntitySelectConfig = UseEntitySearchConfig;
type EntitySearchGroupResult = UseEntitySearchGroupResult;
type EntitySearchPostResult = UseEntitySearchPostResult;
export type EntitySearchTopicResult = UseEntitySearchTopicResult;
type EntitySearchUserResult = UseEntitySearchUserResult;
interface EntitySearchNewResult extends NewSelectOptionProps {
  id: string;
  name: string;
  type: undefined;
}
export type EntitySearchResult =
  | EntitySearchGroupResult
  | EntitySearchPostResult
  | EntitySearchTopicResult
  | EntitySearchUserResult
  | EntitySearchNewResult;

type BaseValue = Pick<EntitySearchResult, 'id' | 'type'>;

type EntitySelectProps<
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
> = Omit<
  SelectProps<
    EntitySearchResult,
    IsMulti,
    false,
    GroupBase<EntitySearchResult>,
    IsClearable
  >,
  'simpleValue' | 'value'
> & {
  autoFocus?: boolean;
  config: EntitySelectConfig;
  PostPillComponent?: React.ElementType;
  postPillComponentProps?: Record<string, unknown>;
  value?: ValueType<BaseValue, IsMulti, false> | null;
  onCreateOption?: (input: string) => void;
  isValidNewOption?: (input: string) => boolean;
  containerClassName?: string;
  getNewOptionData?: (
    inputValue: string,
    optionLabel: React.ReactNode,
  ) => EntitySearchResult;
};

// Remove fields that are unnecessary to render <PostBreadcrumbs/>
// to improve performance and suppress warnings from <Select/> because
// topic.ancestors contains circular refs which are not supported by
// react-fast-compare used by <Select/>.
const pruneTopicFields = _.memoize((topic) => ({
  ...topic,
  ancestors: topic.ancestors.map((ancestor: { id: string; name: string }) =>
    _.pick(ancestor, ['id', 'name']),
  ),
}));

const EntitySelect = <
  IsMulti extends boolean = false,
  IsClearable extends boolean = false,
>({
  autoFocus = false,
  config,
  inputValue: controlledInputValue,
  onInputChange: controlledOnInputChange,
  containerClassName,
  ...props
}: EntitySelectProps<IsMulti, IsClearable>) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [menuIsOpen, setMenuIsOpen] = useState(autoFocus);
  const [selfControlledInputValue, setSelfControlledInputValue] = useState('');
  const inputValue = controlledInputValue || selfControlledInputValue;

  const groupsMap = useAllGroupMap({ skip: !config.group });
  const postsMap = useAllSlimPostMap();
  const topicsMap = useAllTopicMap();
  const usersMap = useAllUsersMap();

  const valueToEntity = useCallback(
    (base: BaseValue): EntitySearchResult | undefined => {
      const { type, id } = base;
      let entity;

      if (type === 'group') {
        const object = groupsMap[id];
        entity = object && groupToEntity(object);
      } else if (type === 'post') {
        const object = postsMap[id];
        entity = object && postToEntity(object);
      } else if (type === 'topic') {
        const object = topicsMap[id];
        entity = object && topicToEntity(object);
      } else if (type === 'user') {
        const object = usersMap[id];
        entity = object && userToEntity(object);
      }

      return entity && { ...base, ...entity };
    },
    [groupsMap, postsMap, topicsMap, usersMap],
  );

  const value = useMemo(
    () =>
      props.value &&
      (props.isMulti && Array.isArray(props.value)
        ? _.compact(props.value.map((base) => valueToEntity(base)))
        : valueToEntity(props.value as BaseValue)),
    [props.value, props.isMulti, valueToEntity],
  ) as ValueType<EntitySearchResult, IsMulti, false> | undefined;

  const baseOptions = useEntitySearch(inputValue, config);
  const options: EntitySearchResult[] = useMemo(
    () =>
      baseOptions.map((result) =>
        result.type !== 'post'
          ? result
          : _.updateWith(result, 'post.topics', (topics) =>
              topics.map(pruneTopicFields),
            ),
      ),
    [baseOptions],
  );

  const handleInputChange = (
    newInputValue: string,
    actionMeta: InputActionMeta,
  ) => {
    if (['input-blur', 'menu-close'].includes(actionMeta.action)) return;
    if (controlledOnInputChange) {
      controlledOnInputChange(newInputValue, actionMeta);
    } else {
      setSelfControlledInputValue(newInputValue);
    }
  };

  return (
    <Select<
      EntitySearchResult,
      IsMulti,
      false,
      GroupBase<EntitySearchResult>,
      IsClearable
    >
      autoFocus={autoFocus}
      className={classNames(
        props.isMulti ? styles.multiEntitySelect : styles.entitySelect,
        containerClassName,
      )}
      components={{
        DropdownIndicator: () => null,
        Option: EntitySelectOption,
        MultiValue: props.isMulti ? EntitySelectMultiValue : undefined,
      }}
      filterOption={null}
      getOptionLabel={({ name }: { name: string }) => name}
      getOptionValue={({ id }: { id: string }) => id}
      inputValue={inputValue}
      menuIsOpen={menuIsOpen}
      onInputChange={handleInputChange}
      onMenuClose={() => setMenuIsOpen(false)}
      onMenuOpen={() => setMenuIsOpen(true)}
      openMenuOnClick
      simpleValue={false}
      {...props}
      options={options}
      value={value}
    />
  );
};

export default EntitySelect;
